import { postRequest } from '@/helpers/request';
import colors from '@/colors/colors.module.scss';
import { paletteCategorical } from '@/colors/chart-palettes';
import { datadogLogs } from '@datadog/browser-logs';

const CURRENT_YEAR = new Date().getFullYear();

export default {
  namespaced: true,
  state: () => ({
    allCommodities: [],
    commodityAddEditModalOpen: false,
    deliveryAddEditModalOpen: false,
    commodityRecords: null,
    deliveryRecords: null,
    selectedIndicatorSignal: null,
    selectedCommodity: null,
    verifiedCommentary: [],
    unverifiedCommentary: [],
    selectedUnit: 'USD Per Bushel',
    buyerSmartAdvisorCotData: {},
    buyerSmartAdvisorInterestRateData: {},
    buyerSmartAdvisorFuturesPrices: {},
    showSpread: false,
    buyerSmartAdvisorFuturesSpread: {},
    availableConclusions: ['HOLD', 'BUY'],
    availableSignalStrengths: Array.from({ length: 10 }, (_, i) => i + 1),
    indicators: [
      'RSI',
      'Bollinger Bands',
      'MACD',
      'Simple Moving Average',
      'Commitment of Traders',
      'Interest Rates',
      'Dominant Force',
    ],
    indicatorSignals: [
      'Price Action',
      'Technical',
      'Time Trigger',
      'Funds',
      'Relative Value',
      'Interest Rate',
      'Dominant Force',
      'Overall',
    ],
    chartData: {},

    CHARTS: [{
      mode: 'multiyear',
      name: 'Price Action',
    }, {
      name: 'Technical',
      mode: 'subchart',
      types: ['line', 'mountain', 'candlestick'],
      subCharts: ['RSI', 'Bollinger Bands', 'MACD', 'Simple Moving Average'],
      defaultRange: '6m',
    }, {
      name: 'Time Trigger',
      mode: 'multiyear',
    }, {
      name: 'Funds',
      mode: 'subchart',
      types: ['line', 'mountain', 'candlestick'],
      subCharts: ['Commitment of Traders'],
      hideLatestPrice: true,
      defaultRange: '6m',
    }, {
      name: 'Relative Value',
      mode: 'relative',
      defaultRange: '1m',
    }, {
      name: 'Interest Rate',
      mode: 'subchart',
      types: ['mountain'],
      subCharts: ['US Fed', 'ECB', 'Bank of Canada'],
      defaultRange: '1Y',
    }, {
      mode: 'multiyear',
      name: 'Dominant Force',
    }],
    selectedChartConfig: null,
    selectedChartOptions: null,

    additionalYearsConfig: Array.from({ length: 9 }, (_, i) => CURRENT_YEAR - i).map((year, index) => ({
      text: year,
      value: index,
      ...(index === 0 ? { disabled: true } : null),
      color: paletteCategorical[index % paletteCategorical.length],
    })),
    chartTypesConfig: [{
      text: 'Line Chart',
      type: 'line',
      iconUrl: '/static/organization_profile/svg/buyer-smart-advisor-line-chart.svg',
      value: 0,
    }, {
      text: 'Mountain Chart',
      type: 'mountain',
      iconUrl: '/static/organization_profile/svg/buyer-smart-advisor-mountain-chart.svg',
      value: 1,
    }, {
      text: 'Candlestick Chart',
      type: 'candlestick',
      iconUrl: '/static/organization_profile/svg/buyer-smart-advisor-candlestick-chart.svg',
      value: 2,
    }],
    chartRangeConfig: [{
      value: '1M',
      text: 'Past Month',
    }, {
      value: '6M',
      text: 'Past 6 Months',
    }, {
      value: 'YTD',
      text: 'Year to Date',
    }, {
      value: '1Y',
      text: 'Past 1 Year',
    }, {
      value: '5Y',
      text: 'Past 5 Years',
    }, {
      value: 'MAX',
      text: 'All Time',
    }],
    subChartConfig: [{
      text: 'RSI',
      value: 0,
    }, {
      text: 'Bollinger Bands',
      value: 1,
    }, {
      text: 'MACD',
      value: 2,
    }, {
      text: 'Simple Moving Average',
      value: 3,
    }, {
      text: 'Commitment of Traders',
      value: 4,
    }, {
      text: 'US Fed',
      value: 5,
      countries: [
        'United States',
        'Australia',
        'Singapore',
      ],
    }, {
      text: 'Bank of Canada',
      value: 6,
      countries: ['Canada'],
    }, {
      text: 'ECB',
      value: 7,
      countries: [
        'Belgium',
        'France',
        'Germany',
        'Italy',
        'Norway',
        'Switzerland',
        'Turkey',
        'United Kingdom',
      ],
    }],
    conversionRates: null,
    advancedAnalysisData: null,
  }),
  mutations: {
    setVerifiedCommentary(state, value) {
      state.verifiedCommentary = value;
    },
    setUnverifiedCommentary(state, value) {
      state.unverifiedCommentary = value;
    },
    setAllCommodities(state, commodities) {
      state.allCommodities = commodities;
    },
    setCommodityAddEditModalOpen(state, value) {
      state.commodityAddEditModalOpen = value;
    },
    setDeliveryAddEditModalOpen(state, value) {
      state.deliveryAddEditModalOpen = value;
    },
    setCommodityRecords(state, value) {
      state.commodityRecords = value;
    },
    setDeliveryRecords(state, value) {
      state.deliveryRecords = value;
    },
    setSelectedCommodity(state, value) {
      state.selectedCommodity = value;
    },
    setSelectedUnit(state, value) {
      state.selectedUnit = value;
    },
    setBuyerSmartAdvisorCotData(state, value) {
      state.buyerSmartAdvisorCotData = value;
    },
    setBuyerSmartAdvisorInterestRateData(state, { bank, data }) {
      state.buyerSmartAdvisorInterestRateData[bank] = data;
    },
    setChartData(state, value) {
      state.chartData = value;
    },
    updateBuyerSmartAdvisorFuturesPrices(state, value) {
      state.buyerSmartAdvisorFuturesPrices = {
        ...state.buyerSmartAdvisorFuturesPrices,
        ...value,
      };
    },
    setBuyerSmartAdvisorFuturesSpread(state, value) {
      state.buyerSmartAdvisorFuturesSpread = {
        ...state.buyerSmartAdvisorFuturesSpread,
        ...value,
      };
    },
    setSelectedIndicatorSignal(state, value) {
      state.selectedIndicatorSignal = value;
    },
    setSelectedChartConfig(state, value) {
      state.selectedChartConfig = value;
    },
    setSelectedChartOptions(state, { key, value }) {
      state.selectedChartOptions = {
        ...(state.selectedChartOptions || {}),
        [key]: value,
      };
    },
    setConversionRates(state, rates) {
      state.conversionRates = rates;
    },
    setShowSpread(state, value) {
      state.showSpread = value;
    },
    setAdvancedAnalysisData(state, { key, value }) {
      state.advancedAnalysisData = {
        ...state.advancedAnalysisData,
        [key]: value,
      };
    },
  },

  getters: {
    getConclusionColor: () => (conclusion) => {
      const color = { HOLD: colors.$neutral, BUY: colors.$positive };
      return color[conclusion] || colors.$greyLighten2;
    },

    relatedCommodity: (state) => state.allCommodities.find(
      (commodity) => commodity.ml_name === state.selectedCommodity?.related_ml_commodity,
    ) || null,

    hasChartData: (state) => Boolean(state.chartData?.ohlc?.close?.length)
      && Boolean(state.chartData?.ohlc?.dates?.length),

    selectedChartComparison: (state) => {
      if (!state.selectedChartOptions?.comparison) return null;
      return state.allCommodities.find((c) => c.ml_name === state.selectedChartOptions.comparison);
    },

    selectedChartYears: (state) => {
      if (!state.selectedChartOptions?.years?.length) return null;
      return state.additionalYearsConfig.filter((year, index) => state.selectedChartOptions.years.includes(index));
    },

    selectedChartType: (state) => {
      if (!('type' in state.selectedChartOptions)) return null;
      return state.chartTypesConfig.find((config) => config.value === state.selectedChartOptions.type);
    },

    selectedChartRange: (state) => {
      const today = new Date();
      const startOfYear = new Date(CURRENT_YEAR, 0, 1);
      let startDate;
      let endDate;

      switch (state.chartRangeConfig[state.selectedChartOptions.range].value) {
        case '1M':
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 31);
          endDate = today;
          break;
        case '6M':
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 31 * 6);
          endDate = today;
          break;
        case 'YTD':
          startDate = startOfYear;
          endDate = today;
          break;
        case '1Y':
          startDate = new Date(today);
          startDate.setFullYear(today.getFullYear() - 1);
          endDate = today;
          break;
        case '5Y':
          startDate = new Date(today);
          startDate.setFullYear(today.getFullYear() - 5);
          endDate = today;
          break;
        case 'MAX':
          startDate = new Date(2015, 0, 1);
          endDate = today;
          break;
        default:
          startDate = today;
          endDate = today;
      }

      return [
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
      ];
    },

    selectedSubChart: (state) => {
      if (!('subChart' in state.selectedChartOptions)) return null;
      return state.subChartConfig.find((config) => config.value === state.selectedChartOptions.subChart);
    },

    getDeliveriesForCommodity: (state) => {
      if (!state.selectedCommodity || !state.deliveryRecords) return [];

      return state.deliveryRecords.filter(
        (delivery) => delivery.commodity === state.selectedCommodity?.id,
      ).sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date));
    },

    getActiveDeliveriesForCommodity: (state) => {
      if (!state.selectedCommodity || !state.deliveryRecords) return [];

      return state.deliveryRecords.filter(
        (delivery) => delivery.commodity === state.selectedCommodity?.id && delivery.time_trigger_status !== 'Expired',
      ).sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date));
    },
    nextMonthFormattedDate: () => {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      return `${nextMonth.getFullYear()}-${(nextMonth.getMonth() + 1).toString().padStart(2, '0')}`;
    },

    commodityMap: (state) => {
      const map = {};
      state.allCommodities.forEach((commodity) => {
        if (
          commodity.id
        && commodity.ml_name
        && commodity.primary_exchange
        && commodity.market_analysis_name
        ) {
          map[commodity.ml_name] = {
            exchange_id: commodity.primary_exchange,
            commodity_id: commodity.market_analysis_name,
          };
        }
      });
      return map;
    },
  },

  actions: {

    async updateSelectedComparisonCommodity({ commit, state, dispatch, getters }, commodity) {
      // Note that we are not using a dispatch to getBuyerSmartAdvisorFuturesPrices here (even though API calls
      // are the same in both actions) because we want both commits to happen in one action so that the chart
      // is not re-drawn multiple times.
      if (!(commodity in state.buyerSmartAdvisorFuturesPrices)) {
        try {
          const formattedExpiryDate = getters.nextMonthFormattedDate;

          if (!state.allCommodities.length) {
            await dispatch('fetchAllCommodities');
          }

          const commodityName = commodity.replace(/ /g, '_').toLowerCase();
          const commodityData = state.allCommodities.find((c) => c.ml_name && c.ml_name.toLowerCase() === commodityName);

          if (!commodityData || !commodityData.primary_exchange || !commodityData.market_analysis_name) {
            throw new Error(`Commodity data not found for ${commodity}`);
          }

          const futuresPrices = await postRequest('/budgeting/ai_advisor/get_data_api_futures_data/', {
            exchange_id: commodityData.primary_exchange,
            commodity_id: commodityData.market_analysis_name,
            expiry_date: formattedExpiryDate,
            time_period_days: 1825,
          });

          let processedPrices = {};
          if (futuresPrices && futuresPrices.date && futuresPrices.date.length > 0) {
            const formattedDates = futuresPrices.date
              .map((dateStr) => {
                try {
                  return `${dateStr}T06:00:00+00:00`;
                } catch (error) {
                  return null;
                }
              })
              .filter((date) => date !== null);

            processedPrices = {
              dates: formattedDates,
              open: futuresPrices.open || [],
              close: futuresPrices.close || [],
              high: futuresPrices.high || [],
              low: futuresPrices.low || [],
            };
          }

          const result = {};
          result[commodity] = processedPrices;
          commit('updateBuyerSmartAdvisorFuturesPrices', result);
        } catch (e) {
          this._vm.$snackbar.error(e);
        }
      }

      commit('setSelectedChartOptions', {
        key: 'comparison',
        value: commodity,
      });
    },

    openCommodityAddEditModal({ commit }) {
      commit('setCommodityAddEditModalOpen', true);
    },

    openDeliveryAddEditModal({ commit }) {
      commit('setDeliveryAddEditModalOpen', true);
    },

    selectCommodity({ commit, state, dispatch }, commodity) {
      commit('setSelectedCommodity', state.allCommodities.find((c) => c?.id === commodity?.id) || null);

      const idx = state.indicatorSignals.findIndex((signal) => signal === state.selectedIndicatorSignal);
      dispatch('switchChart', idx === -1 ? 0 : idx);
    },

    switchChart({ commit, state, getters }, indicator) {
      const fullIndicator = state.indicatorSignals[indicator];
      commit('setSelectedIndicatorSignal', fullIndicator);

      const chartConfig = state.CHARTS.find((chart) => chart.name === fullIndicator);
      commit('setSelectedChartConfig', chartConfig);

      commit('setSelectedChartOptions', {
        key: 'years',
        value: [0, 1, 2],
      });

      commit('setSelectedChartOptions', {
        key: 'type',
        value: chartConfig?.types?.length
          ? state.chartTypesConfig.find((config) => config.type === chartConfig.types[0]).value
          : 0,
      });

      commit('setSelectedChartOptions', {
        key: 'range',
        value: chartConfig?.defaultRange
          ? state.chartRangeConfig.findIndex((config) => config.value === chartConfig.defaultRange.toUpperCase())
          : 0,
      });

      commit('setSelectedChartOptions', {
        key: 'subChart',
        value: chartConfig?.subCharts?.length
          ? state.subChartConfig.find((config) => config.text === chartConfig.subCharts[0]).value
          : 0,
      });

      commit('setSelectedChartOptions', {
        key: 'comparison',
        value: getters.relatedCommodity?.ml_name || 'corn',
      });
    },

    async fetchAllCommodities({ commit }) {
      try {
        const commodities = await postRequest('/budgeting/buyer_advisor/commodities/');
        commit('setAllCommodities', commodities.commodities);
        return commodities.commodities;
      } catch (error) {
        this._vm.$snackbar.error(error);
        return null;
      }
    },

    async createCommodity({ dispatch }, payload) {
      try {
        await postRequest('/budgeting/buyer_advisor/create_commodity/', payload);
        await dispatch('getCommoditiesByOrganization');
        await dispatch('selectCommodity', { id: payload.commodity });
        this._vm.$snackbar.success('Commodity created successfully');
        return true;
      } catch (error) {
        this._vm.$snackbar.error(error);
        return false;
      }
    },

    async updateCommodity({ dispatch }, payload) {
      try {
        await postRequest('/budgeting/buyer_advisor/update_commodity/', payload);
        await dispatch('getCommoditiesByOrganization');
        this._vm.$snackbar.success('Commodity updated successfully');
        return true;
      } catch (error) {
        this._vm.$snackbar.error(error);
        return false;
      }
    },

    async deleteCommodity({ dispatch, state }, payload) {
      try {
        await postRequest('/budgeting/buyer_advisor/delete_commodity/', payload);
        await dispatch('getCommoditiesByOrganization');

        // If the deleted commodity is the currently selected commodity then set selected commodity to 0
        if (payload.commodity === state.selectedCommodity?.id) {
          await dispatch('selectCommodity', state.commodityRecords?.length
            ? { id: state.commodityRecords[0].commodity?.id }
            : null);
        }

        this._vm.$snackbar.success('Commodity deleted successfully');
        return true;
      } catch (error) {
        this._vm.$snackbar.error(error);
        return false;
      }
    },

    async getCommoditiesByOrganization({ commit }) {
      try {
        const { commodities } = await postRequest('/budgeting/buyer_advisor/get_commodities_by_organization/');
        commit('setCommodityRecords', commodities);
        return commodities;
      } catch (error) {
        this._vm.$snackbar.error(error);
        return false;
      }
    },

    async getDeliveriesByOrganization({ commit, state, dispatch }) {
      try {
        const { deliveries } = await postRequest('/budgeting/buyer_advisor/get_deliveries/');
        commit('setDeliveryRecords', deliveries);

        if (state.selectedCommodity?.id) {
          await dispatch('getCommentaries', { commodity: state.selectedCommodity.id });
        }
        return deliveries;
      } catch (error) {
        this._vm.$snackbar.error(error);
        return false;
      }
    },

    async createDelivery({ dispatch }, payload) {
      try {
        await postRequest('/budgeting/buyer_advisor/create_delivery/', payload);
        await dispatch('getDeliveriesByOrganization');
        this._vm.$snackbar.success('Delivery created successfully');
        return true;
      } catch (error) {
        this._vm.$snackbar.error(error);
        return false;
      }
    },

    async updateDelivery({ dispatch }, payload) {
      try {
        await postRequest('/budgeting/buyer_advisor/update_delivery/', payload);
        await dispatch('getDeliveriesByOrganization');
        this._vm.$snackbar.success('Delivery updated successfully');
        return true;
      } catch (error) {
        this._vm.$snackbar.error(error);
        return false;
      }
    },

    async deleteDelivery({ dispatch }, payload) {
      try {
        await postRequest('/budgeting/buyer_advisor/delete_delivery/', payload);
        await dispatch('getDeliveriesByOrganization');
        this._vm.$snackbar.success('Delivery deleted successfully');
        return true;
      } catch (error) {
        this._vm.$snackbar.error(error);
        return false;
      }
    },

    async getCommentaries({ commit, state, getters }, payload) {
      try {
        const commentaries = await postRequest('/budgeting/buyer_advisor/get_commentaries/', payload);

        const { verified, not_verified } = commentaries;
        const latestDelivery = getters.getActiveDeliveriesForCommodity[0] || null;

        if (latestDelivery) {
          verified.push({
            signal_type: 'time_trigger',
            signal_type_display: 'Time Trigger',
            signal_strength: 'N/A',
            text: latestDelivery.time_trigger_status,
            decision: latestDelivery.decision,
          });
        }

        commit('setVerifiedCommentary', verified);
        commit('setUnverifiedCommentary', not_verified);
      } catch (error) {
        this._vm.$snackbar.error(error);
      }
    },

    async getFuturesSpread({ commit, state }) {
      try {
        if (!state.selectedCommodity) return;

        const key = state.selectedCommodity?.id;

        const response = await postRequest('/budgeting/buyer_advisor/get_futures_spread/', { commodity: state.selectedCommodity?.id });

        const processedData = {
          ...response['futures_spread_data'],
          timestamp: new Date().toISOString(),
        };

        commit('setBuyerSmartAdvisorFuturesSpread', {
          [key]: processedData,
        });
      } catch (error) {
        this._vm.$snackbar.error(`Error fetching futures spread data: ${error}`);
      }
    },

    async getAdvancedAnalysisData({ commit, state }) {
      try {
        if (!state.selectedCommodity) return;
        const payload = {
          commodity: state.selectedCommodity?.id,
        };
        const response = await postRequest('/budgeting/buyer_advisor/get_advanced_analysis_data/', payload);

        commit('setAdvancedAnalysisData', {
          key: response.commodity,
          value: response['advanced_analysis_data'],
        });
      } catch (error) {
        this._vm.$snackbar.error(error);
      }
    },

    async updateCommentary({ dispatch, state }, payload) {
      try {
        await postRequest('/budgeting/buyer_advisor/update_commentary/', payload);
        await dispatch('getCommentaries', { commodity: state.selectedCommodity?.id });
        this._vm.$snackbar.success('Commentary updated successfully');
        return true;
      } catch (error) {
        this._vm.$snackbar.error(error);
        return false;
      }
    },

    async fetchCadEurConversionRates({ commit }) {
      try {
        const rates = await postRequest('/budgeting/buyer_advisor/get_cad_eur_conversion_rates/');
        commit('setConversionRates', rates);
      } catch (error) {
        this._vm.$snackbar.error(error);
      }
    },

    async getBuyerSmartAdvisorCotData({ commit, state }) {
      try {
        if (!state.selectedCommodity) return null;

        const response = await postRequest(
          '/budgeting/buyer_advisor/get_cot_data/',
          { commodity: state.selectedCommodity?.id },
        );

        if (response && response.cot_data && Object.keys(response.cot_data).length > 0) {
          const commodityKey = state.selectedCommodity.ml_name;
          const transformedData = {
            [commodityKey]: response.cot_data,
          };

          commit('setBuyerSmartAdvisorCotData', transformedData);
          return transformedData;
        }

        return null;
      } catch (error) {
        datadogLogs.logger.error('Unable to Fetch COT from Database', {}, error);
        return null;
      }
    },

    async getBuyerSmartAdvisorInterestRateData({ commit }) {
      const bankMapping = {
        EUR: 'ecb',
        CAD: 'bank_of_canada',
        USD: 'us_fed',
      };

      try {
        const response = await postRequest('/budgeting/buyer_advisor/get_interest_rates/', {});

        if (response && response.interest_rates) {
          Object.entries(bankMapping).forEach(([currency, bankKey]) => {
            if (response.interest_rates[currency]) {
              const formattedData = Object.entries(response.interest_rates[currency])
                .map(([date, value]) => ({ date, value }));
              commit('setBuyerSmartAdvisorInterestRateData', { bank: bankKey, data: formattedData });
            }
          });
          return response.interest_rates;
        }

        return null;
      } catch (error) {
        datadogLogs.logger.error('Unable to Fetch Interest Rates data from Database', {}, error);
        return null;
      }
    },

    async getBuyerSmartAdvisorFuturesPrices({ commit, state }) {
      try {
        if (!state.selectedCommodity) return null;

        const response = await postRequest(
          '/budgeting/buyer_advisor/get_futures_prices/',
          { commodity: state.selectedCommodity?.id },
        );

        if (response && response.futures_prices_data && Object.keys(response.futures_prices_data).length > 0) {
          const commodityKey = state.selectedCommodity.ml_name;

          const transformedData = {
            prices: {
              [commodityKey]: response.futures_prices_data,
            },
          };

          commit('updateBuyerSmartAdvisorFuturesPrices', transformedData.prices);
          return transformedData.prices;
        }

        return null;
      } catch (error) {
        datadogLogs.logger.error('Unable to Fetch Futures Prices data from Database', {}, error);
        return null;
      }
    },
  },
};
